import type {
  File,
  ProjectGalleryCard,
  ProjectGalleryJunctionItem,
  ProjectGalleryPost,
} from '~/types';

/**
 * Reverse-map `post_gallery_items` junction rows: file id → project post id.
 * Rows missing either side are skipped.
 */
export function reverseMapFilesToProjects(
  junctionRows: ProjectGalleryJunctionItem[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of junctionRows ?? []) {
    if (!row.directus_files_id || !row.posts_id) continue;
    map.set(row.directus_files_id, row.posts_id);
  }
  return map;
}

/** A project post is only eligible for the project gallery when published. */
export function isPublishedProject(post: ProjectGalleryPost): boolean {
  return post?.type === 'project' && post?.status === 'published';
}

/** Deduplicate files by id, preserving first-appearance order. */
export function dedupeFiles(files: File[]): File[] {
  const seen = new Set<string>();
  const result: File[] = [];
  for (const file of files ?? []) {
    if (!file?.id || seen.has(file.id)) continue;
    seen.add(file.id);
    result.push(file);
  }
  return result;
}

/**
 * Resolve the cover image for a project: `post.image` (object or id string)
 * → first gallery file → null.
 */
export function resolveCover(
  post: ProjectGalleryPost,
  gallery: File[],
): File | null {
  const image = post?.image;
  if (image && typeof image === 'object' && 'id' in image) {
    return image as File;
  }
  if (typeof image === 'string') {
    return { id: image } as File;
  }
  return gallery[0] ?? null;
}

/**
 * Build a single normalized card from a project post (used by the
 * `showAllProjects` mode where there is no block file order to preserve).
 */
export function buildCardFromPost(post: ProjectGalleryPost): ProjectGalleryCard | null {
  if (!post?.id || !isPublishedProject(post)) return null;

  const gallery = dedupeFiles(
    (post.gallery ?? [])
      .map((item) => item?.directus_files_id)
      .filter((f): f is File => !!f && typeof f === 'object' && 'id' in f),
  );
  if (gallery.length === 0) return null;

  return {
    postId: post.id,
    title: post.title ?? null,
    client: post.client ?? null,
    slug: post.slug ?? null,
    summary: post.summary ?? null,
    built_with: post.built_with ?? null,
    cost: post.cost ?? null,
    cover: resolveCover(post, gallery),
    gallery,
  };
}

/**
 * Build normalized project cards from the reverse-map junction rows and the
 * fetched project posts, preserving block editor order and deduplicating by
 * project. Posts that are missing, unpublished, or have no gallery files are
 * skipped.
 */
export function buildProjectCards({
  junctionRows,
  posts,
  blockFileIds,
}: {
  junctionRows: ProjectGalleryJunctionItem[];
  posts: ProjectGalleryPost[];
  blockFileIds: string[];
}): ProjectGalleryCard[] {
  const fileToPost = reverseMapFilesToProjects(junctionRows);
  const postsById = new Map<string, ProjectGalleryPost>();
  for (const post of posts ?? []) {
    if (post?.id) postsById.set(post.id, post);
  }

  const seenPosts = new Set<string>();
  const cards: ProjectGalleryCard[] = [];

  for (const fileId of blockFileIds ?? []) {
    const postId = fileToPost.get(fileId);
    if (!postId || seenPosts.has(postId)) continue;

    const post = postsById.get(postId);
    if (!post) continue;

    const card = buildCardFromPost(post);
    if (!card) continue;

    seenPosts.add(postId);
    cards.push(card);
  }

  return cards;
}
